import { AnalyticsService } from 'base/analytics';
import { exists } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { getShadeKey } from 'base/styleguide/concept';
import { Avatar } from 'base/ui/avatar';
import { Banner } from 'base/ui/banner';
import { Button, ButtonPalette, RadioButtonGroup } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconClear,
  IconComment,
  IconConcept,
  IconDesktop,
  IconExternal,
  IconHiddenOutline,
  IconMobile,
  IconVisibleOutline,
} from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { MenuButton } from 'base/ui/menu';
import { menuItem } from 'base/ui/menu_model';
import { Textarea } from 'base/ui/textarea';
import { ToastQueue } from 'base/ui/toast';
import { ALL_TOKENS } from 'base/ui/tokens';
import {
  TextMedium,
  TextRegularLight,
  TextSmall,
  TextSmallLight,
  Title1,
  Title5,
  Title6,
} from 'base/ui/typography';
import { useOnMount } from 'base/useOnMount';
import { useStableCallback } from 'base/useStableCallback';
import { BlocksService, ProjectOwner } from 'blocks/blocks_service';
import { unrefSection } from 'blocks/editor/model_helpers';
import { SelectionStore } from 'blocks/editor/selection/store';
import { SelectionActions } from 'blocks/editor/selection_actions';
import { ProjectSession } from 'blocks/editor/session/session';
import {
  ConceptAPI,
  getGlobalConceptsStore,
} from 'blocks/editor/styleguide/concept_store';
import { HomepagePreview } from 'blocks/editor/styleguide/homepage_preview';
import { PreviewFrame } from 'blocks/editor/styleguide/PreviewFrame';
import { useConceptStyleGuideVariables } from 'blocks/editor/styleguide/style_application/concept_styleguide';
import { useScaledElement } from 'blocks/editor/styleguide/useScaledElement';
import classNames from 'classnames';
import clamp from 'lodash/clamp';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { UseProjectOwner } from '../editor';
import styles from './concept_previews.css';

export const ConceptPreviews = observer(function _ConceptPreviews({
  analytics,
  session,
  selection,
  selectionActions,
  library,
  projectOwner,
  blocksService,
  editing,
  toastQueue,
}: {
  session: ProjectSession;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  library: LibraryCache;
  projectOwner: UseProjectOwner;
  blocksService: BlocksService;
  editing: boolean;
  toastQueue: ToastQueue;
}) {
  const [expandedSlotNumber, setExpandedSlotNumber] = useState<number>();
  const conceptsStore = getGlobalConceptsStore();
  const galleryPositions = [1, 2, 3];

  const conceptsPreviews: ({ galleryPosition: number } & (
    | {
        concept: ConceptAPI;
      }
    | {
        concept?: undefined;
      }
  ))[] = galleryPositions.map((galleryPosition) => {
    const concept = conceptsStore.concepts.find(
      (concept) => concept.state.galleryPosition === galleryPosition,
    );
    if (concept == null || (!editing && concept.state.hidden)) {
      // No concept in this position
      return { galleryPosition };
    }

    return {
      galleryPosition: galleryPosition,
      concept,
    };
  });

  // If the editor hasn't specified which concepts to show in shared concepts,
  // display the first n (n <=3) available concepts from all concepts.
  const filledConceptsPreviews =
    conceptsPreviews.filter((preview) => preview.concept).length > 0
      ? [...conceptsPreviews]
      : galleryPositions.map((galleryPosition) => {
          const concept = conceptsStore.concepts[galleryPosition - 1];
          if (!concept) {
            // Not enough concepts to fill all slots
            return { galleryPosition };
          }

          return {
            galleryPosition: galleryPosition,
            concept,
          };
        });

  useOnMount(() => {
    if (!editing) {
      return;
    }

    // If there are any empty slots, fill them with the first few concepts
    for (let i = 0; i < conceptsPreviews.length; i++) {
      const conceptPreview = conceptsPreviews[i];
      if (conceptPreview.concept != null) {
        continue;
      }

      // Find the first concept that doesn't have a position
      const candidateConcept = conceptsStore.allConcepts.find(
        (concept) => concept.galleryPosition == null,
      );
      if (candidateConcept == null) {
        return; // All concepts have a position
      }

      candidateConcept.galleryPosition = i + 1;
    }
  });

  const expandedConceptPreview = expandedSlotNumber
    ? filledConceptsPreviews.find(
        (conceptPreview) =>
          conceptPreview.galleryPosition === expandedSlotNumber,
      )
    : null;

  const canCycleExpandedConcept =
    expandedConceptPreview != null &&
    filledConceptsPreviews.filter((x) => x.concept != null).length > 1;

  // This is more convoluted than it needs to be, but we need to handle
  // wrapping around the front/end of the list and also when there are
  // empty slots in the list
  const cycleExpandedConcept = useStableCallback(
    (direction: 'forward' | 'backward') => {
      if (!canCycleExpandedConcept) {
        return;
      }

      const conceptPreviewsWithConcepts = filledConceptsPreviews.filter(
        (x) => x.concept != null,
      );

      const delta = direction === 'forward' ? 1 : -1;
      const currentIndex = conceptPreviewsWithConcepts.findIndex(
        ({ galleryPosition }) =>
          galleryPosition === expandedConceptPreview.galleryPosition,
      );

      if (currentIndex === -1) {
        return;
      }

      let targetIndex =
        (currentIndex + delta) % conceptPreviewsWithConcepts.length;

      if (targetIndex < 0) {
        targetIndex += conceptPreviewsWithConcepts.length;
      }

      const targetGalleryPosition =
        conceptPreviewsWithConcepts[
          clamp(targetIndex, 0, conceptPreviewsWithConcepts.length - 1)
        ].galleryPosition;

      setExpandedSlotNumber(targetGalleryPosition);
    },
  );

  return (
    <main className={styles.main}>
      <span className={styles.title}>
        <TextSmall color="grey">Pitch Concepts</TextSmall>
        <span className={styles.hideOnMobile}>
          <Title1>Top three concepts</Title1>
        </span>
        <span className={styles.showOnMobile}>
          <Title5>Top three concepts</Title5>
        </span>
      </span>
      <span className={classNames(styles.blurb, styles.hideOnMobile)}>
        {editing ? (
          <TextRegularLight color="grey">
            We recommend choosing three distinct concepts for feedback to gain
            insights before moving into detail.
          </TextRegularLight>
        ) : (
          <TextRegularLight color="grey">
            Leave comments on what you like or dislike about each concept to
            help move things forward
          </TextRegularLight>
        )}
      </span>
      {filledConceptsPreviews.map(({ concept, galleryPosition }) =>
        concept != null ? (
          <ConceptPreview
            analytics={analytics}
            key={concept.state.uuid}
            session={session}
            selection={selection}
            selectionActions={selectionActions}
            library={library}
            galleryPosition={galleryPosition}
            concept={concept}
            onExpandConcept={setExpandedSlotNumber}
            blocksService={blocksService}
            editing={editing}
          />
        ) : (
          <ConceptPreview
            analytics={analytics}
            key={galleryPosition}
            session={session}
            selection={selection}
            selectionActions={selectionActions}
            library={library}
            galleryPosition={galleryPosition}
            onExpandConcept={setExpandedSlotNumber}
            blocksService={blocksService}
            editing={editing}
          />
        ),
      )}
      {!editing && (
        <div className={styles.banner}>
          <Banner variant="grey3" icon={IconComment}>
            <Inline spacing={8}>
              <TextSmall color="black">Feedback tip</TextSmall>
              <TextSmallLight color="black">
                Give clear feedback about sections styled the way you like and
                which ones need improving and how
              </TextSmallLight>
            </Inline>
          </Banner>
        </div>
      )}
      {expandedConceptPreview && expandedConceptPreview.concept != null ? (
        <ConceptDialog
          analytics={analytics}
          session={session}
          selection={selection}
          selectionActions={selectionActions}
          library={library}
          concept={expandedConceptPreview.concept}
          onClose={() => setExpandedSlotNumber(undefined)}
          projectOwner={projectOwner}
          blocksService={blocksService}
          cycleExpandedConcept={
            canCycleExpandedConcept ? cycleExpandedConcept : undefined
          }
        />
      ) : null}
    </main>
  );
});

const ConceptPreview = observer(function _ConceptPreview({
  analytics,
  session,
  selection,
  selectionActions,
  concept,
  library,
  galleryPosition,
  onExpandConcept,
  blocksService,
  editing,
  projectOwner,
}: {
  analytics: AnalyticsService;
  session: ProjectSession;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  library: LibraryCache;
  galleryPosition: number;
  onExpandConcept: (slotNumber: number) => void;
  blocksService: BlocksService;
  editing: boolean;
  projectOwner?: ProjectOwner;
} & (
  | {
      concept: ConceptAPI;
    }
  | {
      concept?: undefined;
    }
)) {
  const conceptsStore = getGlobalConceptsStore();
  const conceptIndex = concept ? conceptsStore.concepts.indexOf(concept) : null;

  return (
    <Rows spacing={editing ? 16 : 12}>
      <div
        className={classNames(
          styles.preview,
          concept?.state.hidden && styles.hidden,
          concept == null && styles.empty,
        )}
      >
        {concept && (
          <>
            <PreviewImage
              analytics={analytics}
              session={session}
              selection={selection}
              selectionActions={selectionActions}
              library={library}
              concept={concept}
              blocksService={blocksService}
            />
            <div
              className={styles.actions}
              onClick={() => onExpandConcept(galleryPosition)}
            >
              <Button
                icon={IconExternal}
                label="Full preview"
                variant="secondary"
              />
            </div>
          </>
        )}
      </div>
      {editing ? (
        <>
          <Inline xalign="space-between" spacing={8}>
            <MenuButton
              menu={[
                ...conceptsStore.concepts.map((concept, index) =>
                  menuItem({
                    label: `Concept ${index + 1}`,
                    onClick: () =>
                      conceptsStore.setGalleryPosition(
                        concept.state.uuid,
                        galleryPosition,
                      ),
                    icon: IconConcept,
                    checked: concept.state.galleryPosition === galleryPosition,
                  }),
                ),
              ]}
              label={
                concept == null || conceptIndex == null
                  ? 'No more concepts'
                  : `Concept ${conceptIndex + 1}`
              }
              iconRight={IconChevronDown}
              icon={IconConcept}
              search="hidden"
              xalign="left"
              variant="tertiary"
              background="grey3"
              disabled={concept == null}
              fullWidth
            />
            {concept && (
              <Button
                variant="secondary"
                background={concept.state.hidden ? 'white' : 'grey3'}
                icon={
                  concept.state.hidden ? IconHiddenOutline : IconVisibleOutline
                }
                tooltip={
                  concept.state.hidden
                    ? 'Make concept visible'
                    : 'Hide this concept'
                }
                onClick={() => {
                  if (concept == null) {
                    return;
                  }
                  concept.setHidden(!concept.state.hidden);
                }}
              />
            )}
          </Inline>
          <TextSmallLight tag="div">
            <Textarea
              variant="light"
              placeholder="Add a description…"
              value={concept?.state.description ?? ''}
              onChange={(description) => {
                if (concept == null) {
                  return;
                }
                concept.setDescription(description);
              }}
              minHeight={80}
              maxLength={200}
              disabled={concept == null}
            />
          </TextSmallLight>
        </>
      ) : concept != null && conceptIndex != null ? (
        <Rows spacing={4}>
          <TextMedium>Concept {conceptIndex + 1}</TextMedium>
          <TextSmallLight>{concept.state.description}</TextSmallLight>
        </Rows>
      ) : (
        <TextMedium color="grey">Concept in progress</TextMedium>
      )}
    </Rows>
  );
});

const ConceptDialog = observer(function _ConceptDialog({
  analytics,
  session,
  selection,
  selectionActions,
  library,
  concept,
  onClose,
  projectOwner,
  cycleExpandedConcept,
  blocksService,
}: {
  analytics: AnalyticsService;
  session: ProjectSession;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  library: LibraryCache;
  concept: ConceptAPI;
  onClose: () => void;
  projectOwner: UseProjectOwner;
  blocksService: BlocksService;
  cycleExpandedConcept?: (direction: 'forward' | 'backward') => void;
}) {
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>(
    'desktop',
  );
  const isTabletOrSmaller = window.matchMedia(
    `(max-width: ${ALL_TOKENS.breakpointTablet})`,
  ).matches;
  const conceptVariables = useConceptStyleGuideVariables(concept);
  const conceptIndex = getGlobalConceptsStore().concepts.indexOf(concept);
  if (concept == null) {
    return null;
  }

  const [team, creator] =
    projectOwner.loading || projectOwner.error
      ? [null, null]
      : [projectOwner.data?.team, projectOwner.data?.creator];
  return (
    <Dialog
      onClose={onClose}
      height={isTabletOrSmaller ? undefined : 'bleed'}
      fixedWidth={!isTabletOrSmaller}
      width={isTabletOrSmaller ? 'fullscreen' : 'regular'}
      controls={
        <>
          {!isTabletOrSmaller && (
            <Rows spacing={8}>
              <Button
                icon={IconClear}
                onClick={onClose}
                tooltip="Close"
                tooltipPlacement="horizontal"
              />
              {cycleExpandedConcept != null && (
                <>
                  <Button
                    icon={IconChevronRight}
                    onClick={() => cycleExpandedConcept('forward')}
                    tooltip="Next concept"
                    tooltipPlacement="horizontal"
                  />
                  <Button
                    icon={IconChevronLeft}
                    onClick={() => cycleExpandedConcept('backward')}
                    tooltip="Previous concept"
                    tooltipPlacement="horizontal"
                  />
                </>
              )}
            </Rows>
          )}
        </>
      }
    >
      <div className={styles.dialog}>
        <div className={styles.header}>
          <Inline yalign="center">
            <div className={styles.avatarContainer}>
              <Avatar
                size="xlarge"
                avatar={creator?.avatar}
                isAnonymous={creator == null}
                shape="circle"
                color={creator?.color}
                firstName={creator?.firstName}
                lastName={creator?.lastName}
              />
              {team && team.avatar && (
                <div className={styles.teamAvatarOverlay}>
                  <Avatar
                    size="tiny"
                    avatar={team?.avatar}
                    isAnonymous={creator == null}
                    shape="circle"
                  />
                </div>
              )}
            </div>

            <div className={styles.headerContent}>
              <Rows spacing={0}>
                <Title6>Concept {conceptIndex + 1}</Title6>
                <TextSmallLight color="grey">
                  Created by{' '}
                  {creator
                    ? `${creator.firstName} ${creator.lastName}`
                    : 'Anonymous'}
                </TextSmallLight>
              </Rows>
              <div className={styles.showOnMobile}>
                <Button icon={IconClear} variant="tertiary" onClick={onClose} />
              </div>
            </div>
          </Inline>
          <TextSmallLight color="grey">
            {concept.state.description !== ''
              ? concept.state.description
              : null}
          </TextSmallLight>
        </div>
        <div
          className={styles.homepage}
          style={{
            ...conceptVariables,
          }}
        >
          <HomepagePreview
            analytics={analytics}
            session={session}
            selection={selection}
            selectionActions={selectionActions}
            library={library}
            breakpoint={previewMode}
            concept={concept}
            blocksService={blocksService}
          />
        </div>
      </div>
      <div className={styles.previewMode}>
        <ButtonPalette isFullHeight isDarker={previewMode === 'mobile'}>
          <RadioButtonGroup<'mobile' | 'desktop'>
            options={[
              {
                value: 'desktop',
                icon: IconDesktop,
                tooltip: 'Desktop',
              },
              {
                value: 'mobile',
                icon: IconMobile,
                tooltip: 'Mobile',
              },
            ]}
            value={previewMode}
            onChange={setPreviewMode}
          />
        </ButtonPalette>
      </div>
    </Dialog>
  );
});

export const PreviewImage = observer(function _PreviewImage({
  analytics,
  session,
  selection,
  selectionActions,
  library,
  concept,
  blocksService,
  size = 'regular',
}: {
  analytics: AnalyticsService;
  session: ProjectSession;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  library: LibraryCache;
  concept: ConceptAPI;
  blocksService: BlocksService;
  size?: 'regular' | 'small';
}) {
  // If the scale is too small it causes Mobile Safari to use too much memory
  // and that crashes the page.
  // Using a reasonable min scale that will work for desktop and look good enough on mobile.
  const scaleOptions = { minScale: 0.2 };
  const {
    elementRef: containerElementRef,
    elementStyle: containerElementStyle,
  } = useScaledElement(scaleOptions);
  const { elementRef: desktopElementRef, elementStyle: desktopElementStyle } =
    useScaledElement(scaleOptions);
  const { elementRef: mobileElementRef, elementStyle: mobileElementStyle } =
    useScaledElement(scaleOptions);

  const [firstNonNavbarSection] = session.project.homePage.sections
    .map((sref) => {
      const element = unrefSection(sref, session.project)?.element.current;

      if (element?.id == null || element.source.slug.startsWith('navbar')) {
        return null;
      }

      return {
        id: element.id,
        slug: element.source.slug,
      };
    })
    .filter(exists);

  const schemeOverride =
    firstNonNavbarSection != null
      ? concept.state.schemeOverrideByElementId.get(firstNonNavbarSection.id)
      : null;

  const scheme =
    concept.schemes.find((scheme) => getShadeKey(scheme) === schemeOverride) ??
    concept.schemes[concept.state.theme === 'light' ? 0 : 6];

  const background = getShadeKey({
    ...scheme,
    shade: scheme.shade < 4 ? (scheme.palette === 'neutral' ? 5 : 6) : 2,
  });

  return (
    <div
      className={styles.preview}
      ref={containerElementRef}
      style={{
        ...useConceptStyleGuideVariables(concept),
        ...containerElementStyle,
        position: 'absolute',
        background: `var(--${background})`,
      }}
    >
      <div
        className={
          size === 'small'
            ? styles.fixedSizePreviewSmall
            : styles.fixedSizePreview
        }
      >
        <div className={classNames(styles.desktop)}>
          <PreviewFrame>
            <div ref={desktopElementRef} style={desktopElementStyle}>
              {concept ? (
                <HomepagePreview
                  analytics={analytics}
                  session={session}
                  selection={selection}
                  selectionActions={selectionActions}
                  library={library}
                  breakpoint="desktop"
                  sectionLimit={4}
                  concept={concept}
                  blocksService={blocksService}
                />
              ) : null}
            </div>
          </PreviewFrame>
        </div>
        <div className={classNames(styles.mobile)}>
          <PreviewFrame>
            <div ref={mobileElementRef} style={mobileElementStyle}>
              {concept ? (
                <HomepagePreview
                  analytics={analytics}
                  session={session}
                  selection={selection}
                  selectionActions={selectionActions}
                  library={library}
                  breakpoint="mobile"
                  sectionLimit={4}
                  concept={concept}
                  blocksService={blocksService}
                />
              ) : null}
            </div>
          </PreviewFrame>
        </div>
      </div>
    </div>
  );
});
